/* ============================================================================
   configuratore-core.js — Motore UNICO dei configuratori Fotorecord
   ----------------------------------------------------------------------------
   Logica CONDIVISA e identica fra i 4 configuratori (aste / montature / pannelli
   / opere), estratta qui una volta sola. Ogni configuratore diventa un
   DESCRITTORE sottile che fornisce i pezzi specifici (steps, prezzo, properties)
   e delega al core il resto.

   Chiave universale: id_db (vedi scripts/shared_paths.to_id_db). Il profilo si
   matcha per id_db (profilo.sku === product.handle === id_db), niente piu' schemi
   per-tipo divergenti.

   Espone window.FOTORECORD.core = { pickBucket, ctaState, addToCart }.
   Questo file e' PURO sulla parte logica (testabile in Node): nessun accesso al
   DOM in pickBucket/ctaState. addToCart usa fetch + window.location.
   ============================================================================ */
(function () {
  'use strict';

  window.FOTORECORD = window.FOTORECORD || {};

  /* ── Carrello nativo: sceglie la fascia di prezzo (variante reale) col prezzo
        >= a quello calcolato (arrotondamento per eccesso al gradino).
        buckets = window.SU_MISURA_BUCKETS = [{ c: <cent>, id: <variantId> }, ...] ── */
  function pickBucket(euro, buckets) {
    if (!(euro > 0)) return null;
    const cents = Math.round(euro * 100);
    let best = null;
    for (const b of (buckets || [])) {
      if (b.c >= cents && (!best || b.c < best.c)) best = b;
    }
    return best;
  }

  /* ── Macchina a stati UNICA della CTA. Restituisce { text, mode, disabled }.
        Sostituisce le 3 implementazioni divergenti (_renderPrice/_updateCta) e
        incorpora i fix del 23/06: nessun crash quando result e' null/incompleto
        (soloPreventivo letto in sicurezza), guardia prima dei rami fascia.
        - result: oggetto calcolo { soloPreventivo, ... } | null (config incompleta)
        - bucket: fascia scelta da pickBucket | null
        - incompleteText: testo per-configuratore quando manca la config
        - forcePreventivo: flag globale opzionale ── */
  function ctaState(opts) {
    opts = opts || {};
    const result = opts.result;
    const bucket = opts.bucket;
    const incompleteText = opts.incompleteText || 'Completa la configurazione';

    if (opts.forcePreventivo) {
      return { text: 'Richiedi preventivo', mode: 'preventivo', disabled: false };
    }
    if (opts.esaurito) {
      return { text: 'Richiedi disponibilità', mode: 'preventivo', disabled: false, esaurito: true };
    }
    if (!result) {
      return { text: incompleteText, mode: 'preventivo', disabled: true };
    }
    if (result.soloPreventivo) {
      return { text: 'Richiedi preventivo', mode: 'preventivo', disabled: false };
    }
    if (bucket) {
      return { text: 'Aggiungi al carrello', mode: 'cart', disabled: false };
    }
    return { text: 'Richiedi Preventivo Gratuito', mode: 'preventivo', disabled: false };
  }

  /* ── Add-to-cart NATIVO (piano Basic): aggiunge la variante "fascia di prezzo"
        col prezzo reale + properties testuali. Una spedizione sola, nessun Draft
        Order. Reindirizza al carrello.
        Se una property ha valore File/Blob (es. "Foto da stampare", "_anteprima")
        passa a multipart/FormData → Shopify carica il file nativamente e mette
        nella property l'URL CDN (verificato 28/06/2026, nessun servizio esterno). ── */
  function hasFileProps(items) {
    return items.some(it => it && it.properties &&
      Object.keys(it.properties).some(k => it.properties[k] instanceof Blob));
  }

  function buildFormData(items) {
    const fd = new FormData();
    items.forEach((it, i) => {
      fd.append('items[' + i + '][id]', it.id);
      fd.append('items[' + i + '][quantity]', it.quantity != null ? it.quantity : 1);
      const props = it.properties || {};
      Object.keys(props).forEach(k => {
        const v = props[k];
        if (v == null) return;
        if (v instanceof Blob) {
          const ext  = (v.type && v.type.indexOf('png') >= 0) ? 'png' : 'jpg';
          const name = (v instanceof File && v.name) ? v.name
                     : k.replace(/[^a-z0-9]/gi, '_').replace(/^_+/, '') + '.' + ext;
          fd.append('items[' + i + '][properties][' + k + ']', v, name);
        } else {
          fd.append('items[' + i + '][properties][' + k + ']', String(v));
        }
      });
    });
    return fd;
  }

  async function addToCart(opts) {
    const items = (opts && opts.items) || [];
    if (!items.length) throw new Error('addToCart: nessun item');
    let res;
    if (hasFileProps(items)) {
      // niente header Content-Type: il browser imposta da sé il boundary multipart
      res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: buildFormData(items),
      });
    } else {
      res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items }),
      });
    }
    if (!res.ok) throw new Error(await res.text());
    window.location.href = (window.routes && window.routes.cart_url) || '/cart';
  }

  /* ── id_db: chiave universale di un componente = id semantico in minuscolo,
        soli [a-z0-9]. Mirror JS di scripts/shared_paths.to_id_db. I componenti
        (materiali, accessori, passepartout) si referenziano per id_db = toIdDb(sku),
        DERIVATO dallo sku semantico (niente storage). Il PROFILO invece NON e'
        derivabile dal prodotto → la' si usa il metafield custom.id_db. ── */
  function toIdDb(semanticId) {
    return String(semanticId == null ? '' : semanticId).toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  /* ── La forma che legge il cliente: `fras0020` → `FRAS0020`. Mirror JS di
        fotorecord.codice_prodotto.pubblico (docs/convenzioni.md: «il dato è minuscolo,
        la vetrina è maiuscola»). SOLO per il render: il dato resta minuscolo, e una
        property o un confronto in maiuscolo romperebbe ogni join in silenzio. ── */
  function pubblico(idDb) {
    return String(idDb == null ? '' : idDb).trim().toUpperCase();
  }

  /* ── Il nome per il CLIENTE: «FRFO0002 - Crilex», codice pubblico, trattino, nome
        (decisione 21/09/2026; fino a quel giorno «Crilex FRFO0002», codice dopo).
        Mirror di fotorecord.codice_prodotto.nome_cliente: stessa forma su sito, totem,
        preventivi e banco. Senza id_db resta il nome. ── */
  function nomeCliente(nome, idDb) {
    const n = String(nome == null ? '' : nome).trim();
    const c = pubblico(idDb);
    if (!c) return n;
    return n ? c + ' - ' + n : c;
  }

  /* ── Il nome BREVE per il cliente, da un record del configuratore: «FRAS0055 -
        Cornice Sagomata Blu Poro Aperto». Il nome breve lo genera fotorecord.nome_breve
        (Python, SSOT) e arriva come `nomeBreve` (metaobject, via Liquid) o `nome_breve`
        (asset delle aste); se manca — telai, record vecchi — resta il `nome` prima
        della barra del Title. Il codice e' l'id_db dello `sku`. ── */
  function nomeBreveCliente(rec) {
    if (!rec) return '';
    const breve = String(rec.nomeBreve || rec.nome_breve || '').trim();
    const nome = breve || String(rec.nome == null ? '' : rec.nome).split('|')[0].trim();
    return nomeCliente(nome, rec.sku ? toIdDb(rec.sku) : '');
  }

  /* ── Il nome per chi PRELEVA (banco, foglio di produzione): `FRAS0020 - 4/6370-01`.
        Mirror di fotorecord.codice_prodotto.etichetta_staff (decisione 15/09/2026):
        codice pubblico + trattino + codice fornitore; senza fornitore, il solo codice.
        Mai su una superficie del cliente: il codice fornitore resta interno. ── */
  function etichettaStaff(idDb, codiceFornitore) {
    const base = pubblico(idDb);
    const cod = String(codiceFornitore == null ? '' : codiceFornitore).trim();
    if (!base) return cod;
    return cod ? base + ' - ' + cod : base;
  }

  /* ── La didascalia della TILE di un accessorio: «Attaccaglie Oro FRAC0030» — tipo,
        colore e codice pubblico (decisione 16/09/2026). Il nome completo («Attaccaglie
        snodate ottonate 6×13 mm (n.00)») non sta in una cella da 84 px della griglia a
        6 colonne: resta nel tooltip, nella scheda «Accessori» e nel carrello. Le tile
        con lo stesso colore le distingue il codice, come per le aste. ── */
  function didascaliaAccessorio(colore, idDb) {
    const c = String(colore == null ? '' : colore).trim();
    return nomeCliente(c ? 'Attaccaglie ' + c : 'Attaccaglie', idDb);
  }

  /* ── Ridimensiona un URL del CDN Shopify col parametro `width` — l'equivalente JS
        del filtro Liquid `image_url: width: N`, per gli URL che arrivano da un JSON
        (asset dei profili, metafield custom.cfg_profilo, metaobject) e quindi non
        passano da Liquid. Senza, il browser scarica il master a piena risoluzione:
        le foto del profilo sono 2362x2362 (~290 KB) rese in un riquadro da 312 px.

        `w` e' la larghezza CSS massima RADDOPPIATA (retina). Invariati: gli URL non
        Shopify (asset locali dell'app preventivi) e quelli che hanno gia' un width,
        cosi' chiamarla due volte non fa danni. ── */
  function thumbUrl(url, w) {
    const u = String(url == null ? '' : url);
    if (!u || !u.includes('cdn.shopify.com')) return u;
    if (/[?&]width=/.test(u)) return u;
    return u + (u.includes('?') ? '&' : '?') + 'width=' + (w || 500);
  }

  /* ── Un solo formato per gli importi a video, sito + banco + totem (12/09/2026).
        Prima: cinque varianti (base senza simbolo con migliaia, cmg/cpv «€ 12,34»
        senza migliaia, detail-accordion, banco «€\u00a012,34», totem «12,34 €» da
        toLocaleString). fmtNumero e' la parte numerica — virgola decimale, punto per le
        migliaia — e fmtEuro ci mette davanti «€ », come lo mostra il tema. I documenti
        (PDF, sidecar) restano su quote_engine.format_eur, che e' un'altra superficie. ── */
  function fmtNumero(n) {
    const v = Number(n) || 0;
    return v.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }
  function fmtEuro(n) { return '€ ' + fmtNumero(n); }

  /* ── L'ombra di una cornice posata su un fondo, nelle anteprime dal vivo (24/10/2026).
        E' la stessa delle immagini del sito: OWNER `core/fotorecord/composito_parete.py`
        (OMBRA_STRATI, con_ombra); questa e' la sua copia per i canvas, e
        tests/test_ombra_cornice_parete.py la confronta numero per numero. Tre strati
        (spostamento x, spostamento y, sfocatura, opacita'), le prime tre in frazione del
        lato lungo della cornice: una portata morbida verso il basso, una media e un
        contatto stretto lungo i bordi. Andrea: «questa è l'ombra che deve sempre essere
        applicata».
        Disegna SOLO l'ombra del rettangolo (x, y, w, h): il pezzo sta fuori dal canvas e
        l'ombra viene riportata al suo posto dallo spostamento. Ombra e spostamento del
        canvas non seguono la trasformazione del contesto (HiDPI, rotazione dei multifoto),
        quindi tutto si calcola in pixel veri. La sfocatura del canvas e' il doppio della
        deviazione standard, quella di PIL e' la deviazione standard. ── */
  const OMBRA_STRATI = [
    [0.004, 0.040, 0.036, 0.30],
    [0.002, 0.012, 0.010, 0.36],
    [0.000, 0.003, 0.002, 0.50],
  ];
  const COLORE_OMBRA = '14,12,10';
  function ombraCornice(ctx, x, y, w, h) {
    if (!ctx || !(w > 0) || !(h > 0)) return;
    const m = ctx.getTransform ? ctx.getTransform() : { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
    const px = [[x, y], [x + w, y], [x, y + h], [x + w, y + h]]
      .map(([u, v]) => [m.a * u + m.c * v + m.e, m.b * u + m.d * v + m.f]);
    const x0 = Math.min(...px.map((p) => p[0])), x1 = Math.max(...px.map((p) => p[0]));
    const y0 = Math.min(...px.map((p) => p[1])), y1 = Math.max(...px.map((p) => p[1]));
    const lato = Math.max(x1 - x0, y1 - y0);
    const fuori = Math.ceil(x1) + 100;                 // il pezzo, fuori dal canvas a sinistra
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#000';
    for (const [dx, dy, sfocatura, opacita] of OMBRA_STRATI) {
      ctx.shadowColor = 'rgba(' + COLORE_OMBRA + ',' + opacita + ')';
      ctx.shadowBlur = 2 * Math.max(1, sfocatura * lato);
      ctx.shadowOffsetX = fuori + Math.round(dx * lato);
      ctx.shadowOffsetY = Math.round(dy * lato);
      ctx.fillRect(x0 - fuori, y0, x1 - x0, y1 - y0);
    }
    ctx.restore();
  }

  window.FOTORECORD.fmtNumero = fmtNumero;
  window.FOTORECORD.fmtEuro   = fmtEuro;
  window.FOTORECORD.core = {
    pickBucket: pickBucket,
    ctaState: ctaState,
    addToCart: addToCart,
    toIdDb: toIdDb,
    pubblico: pubblico,
    nomeCliente: nomeCliente,
    nomeBreveCliente: nomeBreveCliente,
    etichettaStaff: etichettaStaff,
    didascaliaAccessorio: didascaliaAccessorio,
    thumbUrl: thumbUrl,
    OMBRA_STRATI: OMBRA_STRATI,
    ombraCornice: ombraCornice,
  };
})();
